// Page "Produits" : catalogue de produits/services du tenant.

import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { productsApi } from "../api/products";
import type { Product, ProductCreate, ProductUpdate } from "../api/products";
import { ApiError } from "../api/client";
import { AppShell } from "../components/AppShell";
import { FormField } from "../components/FormField";
import { Modal } from "../components/Modal";
import { CurrencySelect } from "../components/CurrencySelect";
import { useAuthStore } from "../stores/auth";

// ---- Helpers ---------------------------------------------------------------

function calcTTC(ht: number, vat: number): number {
  return parseFloat((ht * (1 + vat / 100)).toFixed(2));
}

function calcHT(ttc: number, vat: number): number {
  return parseFloat((ttc / (1 + vat / 100)).toFixed(2));
}

function fmtPrice(v: string | null | undefined, currency = "EUR") {
  if (v == null) return "—";
  const n = parseFloat(v);
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(n);
}

// ---- Dialog state ----------------------------------------------------------

type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; product: Product };

// ---- Main page -------------------------------------------------------------

export function ProductsPage() {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[] | null>(null);
  const [searchQ, setSearchQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });

  async function reload() {
    setListError(null);
    try {
      const data = await productsApi.list({
        active_only: !showArchived,
        q: searchQ || undefined,
      });
      setProducts(data.items);
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Erreur");
      setProducts([]);
    }
  }

  useEffect(() => {
    void reload();
  }, [searchQ, showArchived]); // eslint-disable-line

  async function onArchive(p: Product) {
    if (!window.confirm(t("productsPage.archiveConfirm"))) return;
    try {
      await productsApi.delete(p.id);
      await reload();
    } catch (err) {
      window.alert(err instanceof ApiError ? err.detail : "Erreur");
    }
  }

  return (
    <AppShell>
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t("productsPage.title")}</h1>
          <p className="text-sm text-slate-500">{t("productsPage.subtitle")}</p>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => setDialog({ mode: "create" })}
        >
          + {t("productsPage.newProduct")}
        </button>
      </header>

      {/* Barre de recherche */}
      <div className="mb-4 flex gap-3 flex-wrap items-center">
        <input
          type="text"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder={t("productsPage.searchPlaceholder")}
          className="input text-sm py-1 min-w-[240px]"
        />
        <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="accent-brand-600"
          />
          Afficher les archivés
        </label>
      </div>

      {/* Liste */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        {listError && (
          <p role="alert" className="p-4 text-sm text-red-600">{listError}</p>
        )}
        {products === null && (
          <p className="p-6 text-sm text-slate-500">{t("common.loading")}</p>
        )}
        {products !== null && products.length === 0 && !listError && (
          <p className="p-6 text-sm text-slate-500">{t("productsPage.noProducts")}</p>
        )}
        {products !== null && products.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">{t("productsPage.colRef")}</th>
                <th className="px-4 py-2 font-medium">{t("productsPage.colName")}</th>
                <th className="px-4 py-2 font-medium">{t("productsPage.colDescription")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("productsPage.colSalePriceHT")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("productsPage.colVat")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("productsPage.colSalePriceTTC")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("productsPage.colPurchasePrice")}</th>
                <th className="px-4 py-2 font-medium">{t("productsPage.colCurrency")}</th>
                <th className="px-4 py-2 font-medium">{t("productsPage.colUnit")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("productsPage.colActions")}</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr
                  key={p.id}
                  className={`border-t border-slate-100 hover:bg-slate-50 ${!p.is_active ? "opacity-50" : ""}`}
                >
                  <td className="px-4 py-2 text-slate-500 text-xs">{p.reference ?? "—"}</td>
                  <td className="px-4 py-2 font-medium text-slate-900">
                    {p.name}
                    {!p.is_active && (
                      <span className="ml-1 text-xs text-slate-400 font-normal">(archivé)</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-500 max-w-xs truncate">{p.description ?? "—"}</td>
                  <td className="px-4 py-2 text-right font-medium text-slate-800">
                    {fmtPrice(p.sale_price_ht, p.currency)}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-600">{p.vat_rate}%</td>
                  <td className="px-4 py-2 text-right font-medium text-slate-800">
                    {fmtPrice(p.sale_price_ttc, p.currency)}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-500">
                    {fmtPrice(p.purchase_price, p.currency)}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{p.currency}</td>
                  <td className="px-4 py-2 text-slate-500">{p.unit ?? "—"}</td>
                  <td className="px-4 py-2 text-right space-x-2">
                    <button
                      type="button"
                      className="text-brand-600 hover:underline"
                      onClick={() => setDialog({ mode: "edit", product: p })}
                    >
                      {t("common.edit")}
                    </button>
                    {p.is_active && (
                      <button
                        type="button"
                        className="text-slate-500 hover:text-red-600 hover:underline"
                        onClick={() => void onArchive(p)}
                      >
                        {t("productsPage.archive")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ProductDialog
        state={dialog}
        onClose={() => setDialog({ mode: "closed" })}
        onSaved={async () => {
          setDialog({ mode: "closed" });
          await reload();
        }}
      />
    </AppShell>
  );
}

// ---- Modal création / édition ----------------------------------------------

interface ProductFormValues {
  name: string;
  reference: string;
  description: string;
  sale_price_ht: string;
  vat_rate: string;
  sale_price_ttc: string;
  purchase_price: string;
  currency: string;
  unit: string;
  is_active: boolean;
}

function emptyForm(currency = "EUR"): ProductFormValues {
  return {
    name: "",
    reference: "",
    description: "",
    sale_price_ht: "0",
    vat_rate: "20",
    sale_price_ttc: "0",
    purchase_price: "",
    currency,
    unit: "",
    is_active: true,
  };
}

function productToForm(p: Product): ProductFormValues {
  return {
    name: p.name,
    reference: p.reference ?? "",
    description: p.description ?? "",
    sale_price_ht: p.sale_price_ht,
    vat_rate: p.vat_rate,
    sale_price_ttc: p.sale_price_ttc,
    purchase_price: p.purchase_price ?? "",
    currency: p.currency,
    unit: p.unit ?? "",
    is_active: p.is_active,
  };
}

function ProductDialog({
  state,
  onClose,
  onSaved,
}: {
  state: DialogState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isOpen = state.mode !== "closed";
  const tenantCurrency = useAuthStore.getState().currentTenant?.currency ?? "EUR";

  const [values, setValues] = useState<ProductFormValues>(emptyForm(tenantCurrency));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dernier champ modifié (ht ou ttc) pour le calcul bidirectionnel
  const [lastEdited, setLastEdited] = useState<"ht" | "ttc">("ht");

  useEffect(() => {
    if (state.mode === "create") {
      setValues(emptyForm(tenantCurrency));
    } else if (state.mode === "edit") {
      setValues(productToForm(state.product));
    }
    setError(null);
    setLastEdited("ht");
  }, [state]); // eslint-disable-line

  if (!isOpen) return null;

  function handleVatChange(vatStr: string) {
    const vat = parseFloat(vatStr) || 0;
    let next = { ...values, vat_rate: vatStr };
    if (lastEdited === "ht") {
      const ht = parseFloat(values.sale_price_ht) || 0;
      next.sale_price_ttc = String(calcTTC(ht, vat));
    } else {
      const ttc = parseFloat(values.sale_price_ttc) || 0;
      next.sale_price_ht = String(calcHT(ttc, vat));
    }
    setValues(next);
  }

  function handleHTChange(htStr: string) {
    setLastEdited("ht");
    const ht = parseFloat(htStr) || 0;
    const vat = parseFloat(values.vat_rate) || 0;
    setValues({ ...values, sale_price_ht: htStr, sale_price_ttc: String(calcTTC(ht, vat)) });
  }

  function handleTTCChange(ttcStr: string) {
    setLastEdited("ttc");
    const ttc = parseFloat(ttcStr) || 0;
    const vat = parseFloat(values.vat_rate) || 0;
    setValues({ ...values, sale_price_ttc: ttcStr, sale_price_ht: String(calcHT(ttc, vat)) });
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      const payload: ProductCreate = {
        name: values.name.trim(),
        description: values.description.trim() || null,
        reference: values.reference.trim() || null,
        sale_price_ht: parseFloat(values.sale_price_ht) || 0,
        vat_rate: parseFloat(values.vat_rate) || 0,
        sale_price_ttc: parseFloat(values.sale_price_ttc) || 0,
        purchase_price: values.purchase_price ? parseFloat(values.purchase_price) : null,
        currency: values.currency,
        unit: values.unit.trim() || null,
        is_active: values.is_active,
      };
      if (state.mode === "edit") {
        const update: ProductUpdate = payload;
        await productsApi.update(state.product.id, update);
      } else {
        await productsApi.create(payload);
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Impossible d'enregistrer");
    } finally {
      setPending(false);
    }
  }

  const title =
    state.mode === "edit"
      ? t("productsPage.editProduct")
      : t("productsPage.createProduct");

  return (
    <Modal open={isOpen} onClose={onClose} title={title}>
      <form onSubmit={onSubmit} className="space-y-4">
        {/* Nom + Référence */}
        <div className="grid grid-cols-2 gap-4">
          <FormField label={t("productsPage.name")} htmlFor="prod-name">
            <input
              id="prod-name"
              required
              value={values.name}
              onChange={(e) => setValues({ ...values, name: e.target.value })}
              className="input"
              placeholder="Consultation, Développement…"
            />
          </FormField>
          <FormField label={t("productsPage.reference")} htmlFor="prod-ref">
            <input
              id="prod-ref"
              value={values.reference}
              onChange={(e) => setValues({ ...values, reference: e.target.value })}
              className="input"
              placeholder="REF-001"
            />
          </FormField>
        </div>

        {/* Description */}
        <FormField label={t("productsPage.description")} htmlFor="prod-desc">
          <textarea
            id="prod-desc"
            rows={2}
            value={values.description}
            onChange={(e) => setValues({ ...values, description: e.target.value })}
            className="input"
          />
        </FormField>

        {/* Prix HT / TVA / TTC */}
        <div className="grid grid-cols-3 gap-4">
          <FormField label={t("productsPage.salePriceHT")} htmlFor="prod-ht">
            <input
              id="prod-ht"
              type="number"
              min={0}
              step="0.01"
              value={values.sale_price_ht}
              onChange={(e) => handleHTChange(e.target.value)}
              className="input"
            />
          </FormField>
          <FormField label={t("productsPage.vatRate")} htmlFor="prod-vat">
            <input
              id="prod-vat"
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={values.vat_rate}
              onChange={(e) => handleVatChange(e.target.value)}
              className="input"
            />
          </FormField>
          <FormField label={t("productsPage.salePriceTTC")} htmlFor="prod-ttc">
            <input
              id="prod-ttc"
              type="number"
              min={0}
              step="0.01"
              value={values.sale_price_ttc}
              onChange={(e) => handleTTCChange(e.target.value)}
              className="input"
            />
          </FormField>
        </div>

        {/* Prix achat / Devise / Unité */}
        <div className="grid grid-cols-3 gap-4">
          <FormField label={t("productsPage.purchasePrice")} htmlFor="prod-buy">
            <input
              id="prod-buy"
              type="number"
              min={0}
              step="0.01"
              value={values.purchase_price}
              onChange={(e) => setValues({ ...values, purchase_price: e.target.value })}
              className="input"
              placeholder="0.00"
            />
          </FormField>
          <FormField label={t("productsPage.currency")} htmlFor="prod-currency">
            <CurrencySelect
              id="prod-currency"
              value={values.currency}
              onChange={(v) => setValues({ ...values, currency: v })}
            />
          </FormField>
          <FormField label={t("productsPage.unit")} htmlFor="prod-unit">
            <input
              id="prod-unit"
              value={values.unit}
              onChange={(e) => setValues({ ...values, unit: e.target.value })}
              className="input"
              placeholder="h, jour, pièce…"
            />
          </FormField>
        </div>

        {/* Actif */}
        {state.mode === "edit" && (
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={values.is_active}
              onChange={(e) => setValues({ ...values, is_active: e.target.checked })}
              className="accent-brand-600"
            />
            Produit actif
          </label>
        )}

        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            className="text-slate-500 hover:text-slate-800 text-sm"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={pending} className="btn-primary">
            {pending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
